// src/pages/tareas/renderers/PreguntaExamTestRenderer.js
import React, { useMemo, useEffect } from "react"
import { ArrowUp, ArrowDown, RotateCcw } from "lucide-react"
import { useCourseStructure } from "../../../context/CourseStructureContext"

/** ---------------- Utils ---------------- */
const readOptions = (q) => {
  const ops = q?.opciones
  if (!ops) return []

  const normalize = (o, i) => ({
    id: String(o?.id ?? o?.valor ?? o?.value ?? o?.key ?? (i + 1)),
    texto: o?.texto ?? o?.label ?? o?.text ?? String(o?.valor ?? o?.value ?? ""),
    media: o?.media || null,
  });

  if (Array.isArray(ops)) return ops.map(normalize)
  if (Array.isArray(ops?.opciones)) return ops.opciones.map(normalize)
  if (Array.isArray(ops?.items)) return ops.items.map(normalize)
  return []
}

const getQuestionMedia = (q) => q?.media || q?.opciones?.media || null
const getEnunciado = (q) =>
  q?.enunciado ?? q?.texto ?? q?.pregunta ?? q?.title ?? q?.titulo ?? "Pregunta"

const looksHtml = (text) => /<\/?[a-z][\s\S]*>/i.test(String(text ?? ""))

/** ---------------- Styles ---------------- */
function RendererStyles() {
  const css = `
    :root{
      --pr-border: rgba(229, 231, 235, 0.5);
      --pr-border-active: rgba(37, 99, 235, 0.4);
      --pr-text: #1e293b;
      --pr-muted: #64748b;
      --pr-bg: rgba(255, 255, 255, 0.85);
      --pr-soft: #f8fafc;
      --pr-soft2: #f1f5f9;
      --pr-primary: #2563eb;
      --pr-ring: rgba(37, 99, 235, 0.15);
      --pr-radius: 20px;
      --pr-radius-sm: 14px;
    }

    .pr-wrap{
      color: var(--pr-text);
      width: 100%;
    }

    /* Contenedor tipo "tarjeta de examen" */
    .pr-card{
      border: 1px solid var(--pr-border);
      background: var(--pr-bg);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-radius: var(--pr-radius);
      padding: 24px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0,0,0,0.02);
      max-width: 980px;
      margin: 0 auto;
      transition: all 0.3s ease;
    }

    .pr-head{
      display: flex;
      gap: 16px;
      align-items: flex-start;
      justify-content: space-between;
      padding-bottom: 16px;
      margin-bottom: 20px;
      border-bottom: 1px solid var(--pr-border);
    }

    .pr-title{
      font-weight: 800;
      font-size: 18px;
      line-height: 1.5;
      letter-spacing: -0.3px;
      margin: 0;
      color: #0f172a;
      overflow-wrap: anywhere;
      word-break: break-word;
      hyphens: auto;
    }

    .pr-sub{
      margin-top: 8px;
      font-size: 13.5px;
      color: var(--pr-muted);
      line-height: 1.5;
      overflow-wrap: anywhere;
    }

    .pr-muted{ color: var(--pr-muted); }

    .pr-chip{
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border: 1px solid rgba(37,99,235, 0.2);
      background: rgba(37, 99, 235, 0.05);
      color: var(--pr-primary);
      white-space: nowrap;
    }

    .pr-body{
      display: grid;
      gap: 20px;
    }

    /* Media */
    .pr-media img{
      max-width: 100%;
      display: block;
      border-radius: 16px;
      border: 1px solid var(--pr-border);
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    }
    .pr-media audio{ width: 100%; outline: none; border-radius: 50px; }

    /* Sección de respuesta */
    .pr-sectionTitle{
      font-size: 14px;
      font-weight: 800;
      color: #334155;
      letter-spacing: -0.2px;
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
    }
    .pr-sectionHint{
      margin-top: 4px;
      color: var(--pr-muted);
      font-size: 13px;
      line-height: 1.5;
    }

    /* Opciones tipo card */
    .pr-options{
      display: grid;
      gap: 12px;
    }
    .pr-option{
      display: flex;
      gap: 16px;
      align-items: flex-start;
      padding: 16px;
      border-radius: var(--pr-radius-sm);
      border: 2px solid transparent;
      background: rgba(248, 250, 252, 0.6);
      box-shadow: 0 2px 8px rgba(0,0,0,0.02);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      user-select: none;
    }
    .pr-option:hover{
      background: #ffffff;
      box-shadow: 0 8px 24px rgba(37, 99, 235, 0.08);
      transform: translateY(-2px);
    }
    /* Estilo cuando el input está checked */
    .pr-option:has(input:checked) {
      background: #ffffff;
      border-color: var(--pr-primary);
      box-shadow: 0 4px 16px rgba(37, 99, 235, 0.12);
    }
    .pr-option:active{ transform: translateY(0); }

    /* Input (radio/checkbox) alineado */
    .pr-option input{
      margin-top: 3px;
      transform: scale(1.2);
      flex: 0 0 auto;
      accent-color: var(--pr-primary);
      cursor: pointer;
    }

    .pr-optext{
      display: grid;
      gap: 8px;
      font-size: 15px;
      line-height: 1.6;
      font-weight: 500;
      color: #1e293b;
      overflow-wrap: anywhere;
      word-break: break-word;
      hyphens: auto;
      min-width: 0;
    }

    /* Inputs */
    .pr-input,
    .pr-textarea,
    .pr-select{
      width: 100%;
      border-radius: var(--pr-radius-sm);
      border: 1px solid #cbd5e1;
      background: #ffffff;
      outline: none;
      font-size: 15px;
      line-height: 1.6;
      color: var(--pr-text);
      transition: all 0.2s ease;
      box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
    }

    .pr-input{ padding: 14px 16px; }
    .pr-textarea{
      padding: 16px;
      min-height: 120px;
      resize: vertical;
    }

    .pr-input:focus,
    .pr-textarea:focus,
    .pr-select:focus{
      border-color: var(--pr-primary);
      box-shadow: 0 0 0 4px var(--pr-ring), inset 0 2px 4px rgba(0,0,0,0.01);
      background: #ffffff;
    }

    /* Completar */
    .pr-fill{
      display: grid;
      gap: 12px;
    }
    .pr-fillGrid{
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    /* Ordenamiento */
    .pr-ordHead{
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
      margin-bottom: 12px;
    }

    .pr-btn{
      appearance: none;
      border: 1px solid #e2e8f0;
      background: #ffffff;
      color: var(--pr-text);
      font-weight: 700;
      padding: 10px 14px;
      border-radius: var(--pr-radius-sm);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 14px;
      transition: all 0.2s ease;
      box-shadow: 0 2px 6px rgba(0,0,0,0.03);
    }
    .pr-btn:hover{
      box-shadow: 0 8px 16px rgba(0,0,0,0.06);
      border-color: #cbd5e1;
      transform: translateY(-1px);
    }
    .pr-btn:active{ transform: translateY(1px); box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
    .pr-btn:focus-visible{
      outline: none;
      box-shadow: 0 0 0 4px var(--pr-ring);
      border-color: var(--pr-primary);
    }

    .pr-ordList{
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 12px;
    }
    .pr-ordItem{
      display: grid;
      grid-template-columns: 48px 1fr auto;
      gap: 16px;
      align-items: center;
      border: 1px solid #e2e8f0;
      border-radius: var(--pr-radius-sm);
      padding: 12px 16px 12px 12px;
      background: #ffffff;
      min-width: 0;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0,0,0,0.02);
    }
    .pr-ordItem:hover{
      border-color: #cbd5e1;
      box-shadow: 0 6px 16px rgba(0,0,0,0.04);
    }
    .pr-rank{
      text-align: center;
      font-weight: 800;
      font-size: 14px;
      color: var(--pr-primary);
      background: rgba(37, 99, 235, 0.08);
      border-radius: 10px;
      padding: 8px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }
    .pr-ordText{
      line-height: 1.5;
      font-size: 15px;
      font-weight: 500;
      overflow-wrap: anywhere;
      word-break: break-word;
      min-width: 0;
    }
    .pr-ordBtns{ display: flex; gap: 6px; }
    .pr-ordBtns .pr-btn { padding: 8px; border-radius: 10px; }

    /* Emparejamiento */
    .pr-match{
      display: grid;
      gap: 12px;
    }
    .pr-matchRow{
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      align-items: center;
      border: 1px solid #e2e8f0;
      border-radius: var(--pr-radius-sm);
      padding: 16px;
      background: #ffffff;
      min-width: 0;
      box-shadow: 0 2px 8px rgba(0,0,0,0.02);
      transition: all 0.2s ease;
    }
    .pr-matchRow:hover{
      border-color: #cbd5e1;
      box-shadow: 0 6px 16px rgba(0,0,0,0.04);
    }
    .pr-matchLeft{
      font-weight: 700;
      font-size: 15px;
      line-height: 1.5;
      overflow-wrap: anywhere;
      word-break: break-word;
      min-width: 0;
    }

    @media (max-width: 740px){
      .pr-card{ padding: 16px; border-radius: 16px; }
      .pr-head{ flex-direction: column; align-items: flex-start; gap: 12px; }
      .pr-chip{ align-self: flex-start; }
      .pr-fillGrid{ grid-template-columns: 1fr; }
    }
    @media (max-width: 560px){
      .pr-ordItem{ grid-template-columns: 1fr; padding: 16px; gap: 12px; }
      .pr-ordBtns{ justify-content: flex-end; }
      .pr-matchRow{ grid-template-columns: 1fr; gap: 12px; }
      .pr-rank{ width: fit-content; padding: 6px 14px; margin-bottom: 4px; }
    }
  `
  return <style>{css}</style>
}

/** ---------------- Media ---------------- */
function RenderMedia({ media, toAbsoluteUrl }) {
  if (!media?.type || !media?.url) return null
  const src = toAbsoluteUrl(media.url)
  if (!src) return null

  if (media.type === "image") {
    return (
      <figure className="pr-media" style={{ margin: "10px 0 12px" }}>
        <img src={src} alt={media.alt || "Imagen"} />
        {media.caption ? (
          <figcaption className="pr-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {media.caption}
          </figcaption>
        ) : null}
      </figure>
    )
  }

  if (media.type === "audio") {
    return (
      <figure className="pr-media" style={{ margin: "10px 0 12px" }}>
        <audio controls src={src} />
        {media.caption ? (
          <figcaption className="pr-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {media.caption}
          </figcaption>
        ) : null}
      </figure>
    )
  }

  return null
}

/** ---------------- Enunciado ---------------- */
function Enunciado({ q }) {
  const { toAbsoluteUrl } = useCourseStructure()
  const media = getQuestionMedia(q)
  const text = getEnunciado(q)
  const html = looksHtml(text)

  return (
    <div>
      <h3 className="pr-title">
        {html ? <span dangerouslySetInnerHTML={{ __html: String(text) }} /> : String(text)}
      </h3>

      {/* Si quieres una “nota” o metadata, úsala aquí */}
      {q?.hint ? <div className="pr-sub">{q.hint}</div> : null}

      {media ? <RenderMedia media={media} toAbsoluteUrl={toAbsoluteUrl} /> : null}
    </div>
  )
}

function SectionHeader({ title, hint }) {
  return (
    <div>
      <div className="pr-sectionTitle">{title}</div>
      {hint ? <div className="pr-sectionHint">{hint}</div> : null}
    </div>
  )
}

const safeJson = (v, fallback = null) => {
  if (v == null) return fallback;
  if (typeof v !== "string") return v;
  try { return JSON.parse(v); } catch { return fallback; }
};

const normalizeQuestion = (question) => {
  const base = question || {};

  let opciones = safeJson(base.opciones, base.opciones);
  let respuesta_correcta = safeJson(base.respuesta_correcta, base.respuesta_correcta);

  if (Array.isArray(opciones)) opciones = { opciones };
  if (opciones == null || typeof opciones !== "object") opciones = {};
  if (respuesta_correcta == null || typeof respuesta_correcta !== "object") respuesta_correcta = {};

  if (!Array.isArray(opciones.opciones)) {
    if (Array.isArray(opciones.options)) opciones.opciones = opciones.options;
    else if (Array.isArray(opciones.items)) opciones.opciones = opciones.items;
    else opciones.opciones = [];
  }

  const tipo_pregunta =
    base?.tipo_pregunta ??
    base?.tipo ??
    base?.question_type ??
    "respuesta_corta";

  return { ...base, tipo_pregunta, opciones, respuesta_correcta };
};

const inferSelectionMode = (question) => {
  const q = normalizeQuestion(question);
  const tipo = String(q?.tipo_pregunta || "").toLowerCase();

  if (["seleccion_simple", "single_choice", "multiple_choice"].includes(tipo)) {
    return { isSelection: true, isUnique: true };
  }

  if (["seleccion_multiple", "multiple_select"].includes(tipo)) {
    const rawMax = q?.opciones?.max_seleccion ?? q?.max_seleccion;
    const parsedMax = Number(rawMax);
    if (Number.isFinite(parsedMax) && parsedMax > 0) {
      return { isSelection: true, isUnique: parsedMax === 1 };
    }

    const explicitMultiple =
      q?.opciones?.multiple ??
      q?.multiple ??
      q?.opciones?.permite_multiple ??
      q?.permite_multiple;

    if (typeof explicitMultiple === "boolean") {
      return { isSelection: true, isUnique: !explicitMultiple };
    }

    const correctMany = Array.isArray(q?.respuesta_correcta?.correctas)
      ? q.respuesta_correcta.correctas.filter((v) => v != null && `${v}`.trim() !== "")
      : Array.isArray(q?.respuesta_correcta)
        ? q.respuesta_correcta.filter((v) => v != null && `${v}`.trim() !== "")
        : [];

    if (correctMany.length > 0) {
      return { isSelection: true, isUnique: correctMany.length <= 1 };
    }

    return { isSelection: true, isUnique: false };
  }

  return { isSelection: false, isUnique: false };
};


/** ---------------- Main Renderer ---------------- */
export default function PreguntaExamTestRenderer({ question, value, onChange }) {
  const q = useMemo(() => normalizeQuestion(question), [question]);
  const tipo = String(q?.tipo_pregunta || "").toLowerCase();
  const selectionMode = useMemo(() => inferSelectionMode(q), [q]);

  const tipoLabel = useMemo(() => {
    const map = {
      seleccion_multiple: "Selección",
      verdadero_falso: "Verdadero / Falso",
      respuesta_corta: "Respuesta corta",
      completar: "Completar",
      ordenamiento: "Ordenamiento",
      emparejamiento: "Emparejamiento",
    }
    return map[tipo] || (tipo ? tipo : "Pregunta")
  }, [tipo])

  return (
    <div className="pr-wrap">
      <RendererStyles />

      <div className="pr-card">
        <div className="pr-head">
          <div style={{ minWidth: 0 }}>
            <Enunciado q={question} />
          </div>
          <div className="pr-chip">{tipoLabel}</div>
        </div>

        <div className="pr-body">
          {selectionMode.isSelection && (
            <>
              <SectionHeader title="Selecciona tu respuesta" hint="Toca una opción para marcarla." />
              <SM question={question} value={value} onChange={onChange} forcedUnique={selectionMode.isUnique} />
            </>
          )}

          {tipo === "verdadero_falso" && (
            <>
              <SectionHeader title="Selecciona una opción" />
              <VF question={question} value={value} onChange={onChange} />
            </>
          )}

          {tipo === "respuesta_corta" && (
            <>
              <SectionHeader title="Escribe tu respuesta" hint="Procura ser claro y directo." />
              <RC value={value} onChange={onChange} />
            </>
          )}

          {tipo === "completar" && (
            <>
              <SectionHeader title="Completa los espacios" hint="Rellena cada campo con la información correcta." />
              <CompletarField question={question} value={value} onChange={onChange} />
            </>
          )}

          {tipo === "ordenamiento" && (
            <>
              <SectionHeader title="Ordena los elementos" hint="Usa las flechas para cambiar el orden." />
              <OrdenamientoField question={question} value={value} onChange={onChange} />
            </>
          )}

          {tipo === "emparejamiento" && (
            <>
              <SectionHeader title="Empareja los elementos" hint="Selecciona la correspondencia en cada fila." />
              <EmparejamientoField question={question} value={value} onChange={onChange} />
            </>
          )}

          {![
            "seleccion_multiple",
            "seleccion_simple",
            "single_choice",
            "multiple_choice",
            "verdadero_falso",
            "respuesta_corta",
            "completar",
            "ordenamiento",
            "emparejamiento",
          ].includes(tipo) && (
              <div className="pr-muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                Tipo no soportado: <code>{String(question?.tipo ?? tipo)}</code>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

/** ---------------- Tipos ---------------- */

// Selección múltiple (radio/checkbox), soporta media por opción
function SM({ question, value, onChange, forcedUnique = false }) {
  const { toAbsoluteUrl } = useCourseStructure()
  const ops = readOptions(question)

  // Inferencia robusta de "única" vs "múltiple"
  const inferredMode = inferSelectionMode(question)
  const isUnique = forcedUnique || inferredMode.isUnique
  const qName = `sel_${question?.id ?? "q"}`

  const renderText = (t) => {
    const html = looksHtml(t)
    return html ? <span dangerouslySetInnerHTML={{ __html: String(t) }} /> : <span>{String(t)}</span>
  }

  // -------- ÚNICA (radio) -> value SIEMPRE array de 1 --------
  if (isUnique) {
    const currArr = Array.isArray(value) ? value.map(String) : (typeof value === "string" ? [String(value)] : []);
    const curr = String(currArr[0] ?? "");

    const choose = (sid) => onChange([String(sid)]);

    return (
      <div className="pr-options">
        {ops.map((op) => {
          const sid = String(op.id);
          const checked = curr === sid;

          return (
            <label key={sid} className="pr-option">
              <input
                type="radio"
                name={qName}
                value={sid}
                checked={checked}
                onChange={() => choose(sid)}
              />
              <span className="pr-optext">
                {renderText(op.texto)}
                {op.media ? (
                  <div style={{ maxWidth: 520 }}>
                    <RenderMedia media={op.media} toAbsoluteUrl={toAbsoluteUrl} />
                  </div>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    );
  }


  // -------- MÚLTIPLE (checkbox) --------
  const selected = Array.isArray(value) ? value.map(String) : []
  const toggle = (sid) => {
    const has = selected.includes(sid)
    const next = has ? selected.filter((x) => x !== sid) : [...selected, sid]
    onChange(next)
  }

  return (
    <div className="pr-options">
      {ops.map((op) => {
        const sid = String(op.id)
        const checked = selected.includes(sid)

        return (
          <label key={sid} className="pr-option">
            <input type="checkbox" name={qName} value={sid} checked={checked} onChange={() => toggle(sid)} />
            <span className="pr-optext">
              {renderText(op.texto)}
              {op.media ? (
                <div style={{ maxWidth: 520 }}>
                  <RenderMedia media={op.media} toAbsoluteUrl={toAbsoluteUrl} />
                </div>
              ) : null}
            </span>
          </label>
        )
      })}
    </div>
  )
}

// Verdadero/Falso
function VF({ question, value, onChange }) {
  const opsRaw = readOptions(question);
  const ops =
    opsRaw.length > 0
      ? opsRaw
      : [
        { id: "verdadero", texto: "Verdadero" },
        { id: "falso", texto: "Falso" },
      ];

  const currArr = Array.isArray(value) ? value.map(String) : (typeof value === "string" ? [String(value)] : []);
  const curr = String(currArr[0] ?? "");

  return (
    <div className="pr-options">
      {ops.map((op) => {
        const sid = String(op.id);
        return (
          <label key={sid} className="pr-option">
            <input
              type="radio"
              name={`vf_${question?.id ?? "q"}`}
              checked={curr === sid}
              onChange={() => onChange([sid])}
            />
            <span className="pr-optext">
              <span>{op.texto}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}


// Respuesta corta (mejor como textarea para que preguntas largas no “aprieten” la UI)
function RC({ value, onChange }) {
  const curr = typeof value === "string" ? value : ""
  return (
    <textarea
      className="pr-textarea"
      placeholder="Escribe tu respuesta…"
      value={curr}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

// Completar
function blanksCountFor(question) {
  const texto = question?.opciones?.texto_con_espacios || "";
  const blocks = String(texto).split("_____");
  const provided = Number(question?.opciones?.numero_espacios || 0);
  const desdeTexto = Math.max(0, blocks.length - 1);
  const desdeRespuesta = Array.isArray(question?.respuesta_correcta?.respuestas)
    ? question.respuesta_correcta.respuestas.length
    : 0;
  return Math.max(provided, desdeTexto, desdeRespuesta, 1);
}

function CompletarField({ question, value, onChange }) {
  const texto = question?.opciones?.texto_con_espacios || "";
  const blocks = useMemo(() => String(texto).split("_____"), [texto]);
  const n = blanksCountFor(question);

  const curr = Array.isArray(value) ? value : Array.from({ length: n }, () => "");

  useEffect(() => {
    if (!Array.isArray(value) || value.length !== n) {
      onChange(Array.from({ length: n }, () => ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, n]);

  const setSlot = (i, v) => {
    const next = curr.map((x, idx) => (idx === i ? v : x));
    onChange(next);
  };

  return (
    <div style={{ lineHeight: 1.7 }}>
      {blocks.map((chunk, i) => (
        <React.Fragment key={i}>
          <span style={{ fontSize: ".95rem" }}>{chunk}</span>
          {i < n && (
            <input
              className="pr-input"
              style={{ display: "inline-block", width: "auto", minWidth: "10ch", maxWidth: "28ch", margin: "0 8px 10px" }}
              type="text"
              value={curr[i] || ""}
              onChange={(e) => setSlot(i, e.target.value)}
              placeholder={`#${i + 1}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}


// Ordenamiento
function OrdenamientoField({ question, value, onChange }) {
  const opciones = useMemo(() => {
    const base = question?.opciones?.opciones || []
    return base.map((o, idx) => ({
      id: String(o?.id ?? idx + 1),
      texto: o?.texto ?? o?.label ?? String(o?.value ?? ""),
    }))
  }, [question?.id])

  const curr =
    Array.isArray(value) && value.length === opciones.length ? value.map(String) : opciones.map((o) => o.id)

  useEffect(() => {
    // inicializa cuando cambie la pregunta o cambie el tamaño
    if (!Array.isArray(value) || value.length !== opciones.length) {
      onChange(opciones.map((o) => o.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, opciones.length])

  const move = (idx, dir) => {
    const ni = idx + dir
    if (ni < 0 || ni >= curr.length) return
    const copy = [...curr]
    const [el] = copy.splice(idx, 1)
    copy.splice(ni, 0, el)
    onChange(copy)
  }

  const reset = () => onChange(opciones.map((o) => o.id))

  return (
    <>
      <div className="pr-ordHead">
        <div className="pr-muted" style={{ fontSize: 12.5 }}>
          Orden actual guardado automáticamente
        </div>

        <button onClick={reset} type="button" className="pr-btn" style={{ fontSize: 13 }}>
          <RotateCcw size={16} /> Reiniciar
        </button>
      </div>

      <ul className="pr-ordList">
        {curr.map((id, idx) => {
          const it = opciones.find((o) => o.id === id)
          return (
            <li key={id} className="pr-ordItem">
              <div className="pr-rank">#{idx + 1}</div>
              <div className="pr-ordText">{it?.texto}</div>
              <div className="pr-ordBtns">
                <button type="button" onClick={() => move(idx, -1)} className="pr-btn" aria-label="Subir">
                  <ArrowUp size={16} />
                </button>
                <button type="button" onClick={() => move(idx, +1)} className="pr-btn" aria-label="Bajar">
                  <ArrowDown size={16} />
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}

// Emparejamiento — value como ["aId|bId", ...]
function EmparejamientoField({ question, value, onChange }) {
  const colA = useMemo(() => (question?.opciones?.columna_a || []).map((o, i) => ({
    id: String(o?.id ?? `A${i + 1}`),
    texto: o?.texto ?? String(o?.value ?? ""),
  })), [question?.id]);

  const colB = useMemo(() => (question?.opciones?.columna_b || []).map((o, i) => ({
    id: String(o?.id ?? `B${i + 1}`),
    texto: o?.texto ?? String(o?.value ?? ""),
  })), [question?.id]);

  const curr = useMemo(() => {
    // value esperado: objeto { [aId]: bId }
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
    // fallback: inicial
    return Object.fromEntries(colA.map((a) => [String(a.id), ""]));
  }, [value, colA]);

  useEffect(() => {
    // asegurar keys de colA
    const next = { ...Object.fromEntries(colA.map((a) => [String(a.id), ""])), ...(curr || {}) };
    // limpiar keys que no existan
    for (const k of Object.keys(next)) {
      if (!colA.some((a) => String(a.id) === String(k))) delete next[k];
    }
    // si cambió estructura, rehidratar
    const sameKeys =
      Object.keys(next).length === colA.length &&
      colA.every((a) => Object.prototype.hasOwnProperty.call(next, String(a.id)));
    if (!sameKeys) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, colA.length]);

  return (
    <div className="pr-match">
      {colA.map((a) => (
        <div className="pr-matchRow" key={a.id}>
          <div className="pr-matchLeft">{a.texto}</div>
          <div>
            <select
              value={curr[String(a.id)] || ""}
              onChange={(e) => onChange({ ...(curr || {}), [String(a.id)]: e.target.value })}
              className="pr-select"
            >
              <option value="">Selecciona…</option>
              {colB.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.texto}
                </option>
              ))}
            </select>
          </div>
        </div>
      ))}

      <div className="pr-muted" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.45 }}>
        Consejo: completa todas las filas de la columna A para que el emparejamiento quede guardado.
      </div>
    </div>
  );
}

